<template>

    <div class="col-span-6 grid grid-cols-6">

        <div class="col-span-3 flex flex-col justify-between gap-y-2 items-center">

            <div class="block text-gray-800 mb-5">Launcher position</div>

            <a href="#" title="Bottom Left" @click.prevent="position('bl')"
                class="w-full md:w-2/3 widget widget-left border-2 rounded-lg pl-3 py-5 pr-16 font-bold text-md" :class="{
                    'active bg-blue-500 border-blue-500 text-white': widget_settings.position == 'bl',
                    'bg-white text-gray-500 border-gray-500 opacity-75 hover:bg-gray-500 hover:border-gray-500 hover:text-white hover:opacity-100': widget_settings.position != 'bl'
                }">
                Bottom left
            </a>

            <a href="#" title="Bottom Right" @click.prevent="position('br')"
                class="w-full md:w-2/3 widget widget-right border-2 rounded-lg pl-3 py-5 pr-16 font-bold text-md"
                :class="{
                    'active bg-blue-500 border-blue-500 text-white': widget_settings.position == 'br',
                    'bg-white text-gray-500 border-gray-500 opacity-75 hover:bg-gray-500 hover:border-gray-500 hover:text-white hover:opacity-100': widget_settings.position != 'br'
                }">
                Bottom Right
            </a>

            <a href="#" title="Blank" @click.prevent="position('blank')"
                class="w-full md:w-2/3 widget widget-blank border-2 rounded-lg pl-3 py-5 pr-16 font-bold text-md"
                :class="{
                    'active bg-blue-500 border-blue-500 text-white': widget_settings.position == 'blank',
                    'bg-white text-gray-500 border-gray-500 opacity-75 hover:bg-gray-500 hover:border-gray-500 hover:text-white hover:opacity-100': widget_settings.position != 'blank'
                }">
                Custom launcher<sup>*</sup>
            </a>

        </div>
        <div class="col-span-3">

            <div class="relative flex w-full mt-8">
                <img :src="bg_image" alt="launcher" class="w-full rounded-lg shadow-xl mx-auto" />
                <div class="zest absolute w-14 h-14 rounded-full bottom-2 transition duration-200 shadow-md" :class="{
                    'hidden': widget_settings.position == 'blank',
                    'bl': widget_settings.position == 'bl',
                    'br': widget_settings.position == 'br',
                }" :style="'background-color:' + widget_settings.color"></div>
            </div>
        </div>


        <div class="col-span-3"></div>
        <div class="col-span-3 text-center">

            <div class="flex flex-row justify-center">

                <div class="text-gray-600 my-3" v-if="widget_settings.position == 'blank'">
                    Open from a specific<br />button, link or element in your app!
                </div>
                <div class="text-gray-600 my-3 text-right" v-if="widget_settings.position == 'br'">
                    This is how {{ app_name }} will<br /> appear on your web app!
                </div>
                <div v-if="widget_settings.position == 'br'">
                    <img class="ml-1 mt-2" src="/img/arrow_onboarding.svg" width="60" />
                </div>
                <div v-if="widget_settings.position == 'bl'">
                    <img class="mr-1 mt-2" src="/img/arrow_onboarding.svg" width="60"
                        style="-webkit-transform: scaleX(-1);transform: scaleX(-1);" />
                </div>
                <div class="text-gray-600 my-3 text-left" v-if="widget_settings.position == 'bl'">
                    This is how {{ app_name }} will<br /> appear on your web app!
                </div>

            </div>


        </div>

        <div class="col-span-6 mt-8" v-if="is_modal">
            <div class="flex flex-row justify-center px-4">
                <jet-secondary-button @click="$inertia.visit(route('settings.inapp') + '#launcher')">
                    Settings page for more details
                    <vue-feather type="arrow-right" class="w-4 h-4 ml-2" />
                </jet-secondary-button>
            </div>
        </div>

        <div class="col-span-6" v-else>

            <jet-section-border />

            <div class="flex flex-col justify-center px-4">

                <list-toggle>
                    <template #title>
                        Custom launcher
                    </template>
                    <template #content>
                        <div class="rounded-xl p-5 bg-gray-100">
                            If you’d like the widget to open from a specific button, link or element in your app, you
                            can create a custom launcher. For example, you can trigger the widget to open when a
                            customer clicks on your <em>Send a feedback</em> button or Feedback icon.

                            <p>Note: You can add as many custom launchers to your app or website as you wish.</p>

                            <h6 class="text-gray-900 mt-5 text-lg">How to create a custom launcher on your web app or
                                site?</h6>

                            <p>Simply add the classname <code class="text-green-500 text-sm">zstw_open</code> to one or
                                multiple elements you want to use as a launcher, see 2 examples below:</p>

                            <div class="relative overflow-hidden code_background -ml-5 -mr-5 my-3">
                                <highlightjs class="overflow-auto px-5 py-4 whitespace-pre-wrap text-sm" language='html'
                                    :code="str_example2" />
                            </div>



                            <h6 class="text-gray-900 mt-5 text-lg">Handling the widget with Javascript</h6>

                            <p>In some situations you might prefer using Javascript to command the widget opening, we
                                exposed a few methods at your disposal:</p>
                            <ul class="list-disc list-inside">
                                <li class="mt-2"><span class="text-gray-900">Know the widget status:</span> <code
                                        class="text-green-500 text-sm">window.zest.widget.opened()</code> will return a
                                    <code class="text-green-500 text-sm">boolean</code>.
                                </li>
                                <li class="mt-2"><span class="text-gray-900">Open the widget:</span> <code
                                        class="text-green-500 text-sm">window.zest.widget.open()</code> will open the
                                    widget if not already opened.</li>
                                <li class="mt-2"><span class="text-gray-900">Close the widget:</span> <code
                                        class="text-green-500 text-sm">window.zest.widget.close()</code> will close the
                                    widget if not already closed.</li>
                                <li class="mt-2"><span class="text-gray-900">Toggle open/close automatically:</span>
                                    <code class="text-green-500 text-sm">window.zest.widget.toggle()</code>.
                                </li>
                            </ul>

                            <p class="mt-4">For example:</p>

                            <div
                                class="relative rounded-t-none rounded-b-xl overflow-hidden code_background -ml-5 -mr-5 -mb-5 mt-3">
                                <highlightjs class="overflow-auto px-5 py-4 whitespace-pre-wrap text-sm"
                                    language='javascript' :code="str_example3" />
                            </div>

                            <a name="custom-launcher"></a>

                        </div>

                    </template>
                </list-toggle>

            </div>

        </div>


    </div>

</template>






<script>

import ListToggle from '@/Components/UI/ListToggle'
import JetSectionBorder from '@/Jetstream/SectionBorder'
import JetSecondaryButton from '@/Jetstream/SecondaryButton'

import 'highlight.js/styles/github-dark.css'
import hljsVuePlugin from "@highlightjs/vue-plugin"

export default {

    props: ["w_settings", "str_example2", "str_example3", "is_modal"],

    components: {
        ListToggle, JetSectionBorder,
        highlightjs: hljsVuePlugin.component, JetSecondaryButton,
    },

    data() {
        return {
            widget_settings: this.w_settings,
            onboard_launcher: false,
            app_name: this.$page.props.app_name
        }
    },

    created() {
        if (this.$page.props.user.current_team.metadata) {
            let json = JSON.parse(this.$page.props.user.current_team.metadata)
            if (json.onboard_launcher) {
                this.onboard_launcher = json.onboard_launcher == "yes"
            }
        }
    },
    computed: {
        is_light() {
            return ColorUtil.lightOrDark(this.widget_settings.color) == "light"
        },
        bg_image() {
            return this.widget_settings.position == "blank" ? "/img/custom_launcher.png" : "/img/widget_browser.png"
        }
    },
    mounted() {

    },
    methods: {
        position: function (pos) {
            //console.log("position", pos)
            this.widget_settings.position = pos
            this.update_position(pos)
        },
        update_position: function (pos) {
            let url = this.$page.props.api_url + '/v1/settings/appearance';
            //console.log("updateSettings", $('#hexval').val())
            //initial[0] = $('#hexval').val();
            //$('#hexvalLabel').val(initial[0]);
            var params = {
                w_pos: pos
            };
            axios.post(url, params)
                .then(response => {
                    //console.log(response.data);
                    if (response.data) {
                        this.widget_settings = JSON.parse(response.data.settings)
                        let options = {
                            type: "default",
                            timeout: 2500,
                            position: "top-right",
                            hideProgressBar: false,
                        }
                        this.$moshaToast("Launcher updated!", options)
                        this.set_onboarding_done()
                    }
                })
                .catch(e => {
                    console.error(e);
                });
        },
        set_onboarding_done() {
            if (this.onboard_launcher) {
                //this.EventBus.emit('onboarding_step_validation','onboard_customize');
                return
            }
            let url = this.$page.props.api_url + '/v1/team/set_meta';
            var params = {
                meta: "onboard_launcher",
                value: "yes"
            };
            axios.post(url, params)
                .then(response => {
                    //console.log(response.data);
                    if (response.data) {
                        this.onboard_launcher = true
                        this.EventBus.emit('onboarding_step_validation', 'onboard_launcher');
                    }
                })
                .catch(e => {
                    console.error(e);
                });
        }
    }

}

</script>

<style scoped>
.fade-enter {
    opacity: 0;
}

.fade-enter-active {
    transition: all 0.2s ease-out;
}

.fade-leave-to {
    opacity: 0;
}

.fade-leave-active {
    transition: all 0.2s ease-in;
}



.widget {
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 20%;
    /* opacity: 0.5; */
}

.widget.active {
    opacity: 1;
}

.widget-left {
    background-image: url(/img/widgetl.svg);
}

.widget-left.active,
.widget-left:hover {
    background-image: url(/img/widgetl_w.svg);
}

.widget-right {
    background-image: url(/img/widgetr.svg);
}

.widget-right.active,
.widget-right:hover {
    background-image: url(/img/widgetr_w.svg);
}

.widget-blank {
    background-image: url(/img/widgetb.svg);
}

.widget-blank.active,
.widget-blank:hover {
    background-image: url(/img/widgetb_w.svg);
}

.zest {
    background-image: url(/img/logo-white-birdie.svg);
    background-position: center center;
    background-size: 40%;
    background-repeat: no-repeat;
    -moz-transition: right 0.4s ease-in-out, left 0.4s ease-in-out, opacity 0.2s ease-in-out;
    -webkit-transition: right 0.4s ease-in-out, left 0.4s ease-in-out, opacity 0.2s ease-in-out;
    transition: right 0.4s ease-in-out, left 0.4s ease-in-out, opacity 0.2s ease-in-out;
}

.zest.br {
    left: 81%;
}

.zest.bl {
    left: 4%;
}

.code_background {
    background-color: #172938;
}

.hljs {
    background: transparent !important;
}
</style>
